import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toastNotification } from '../../components/toast/Toast';
import { useFirmContext } from '../../providers/FirmProvider';
import { useCreateWorkpaper } from '../../queries/use-create-workpaper';
import { Entity } from '../../types/entity';
import { TrialBalanceAccountingBasisEnum, WorkpaperRoleEnum } from '../../types/workpaper';
import { UserOverview } from '../../types/user';
import { useForm, FormProvider } from 'react-hook-form';
import { AlertV2, StandardAlert } from '@cimplico/components';
import NewWorkpaperStepOne from './NewWorkpaperStepOne';
import NewWorkpaperStepThree from './NewWorkpaperStepThree';
import NewWorkpaperStepTwo from './NewWorkpaperStepTwo';
import XeroErrorAlert from '../../components/XeroErrorAlert';
import NewWorkpaperStepFour from './NewWorkpaperStepFour';

type NewWorkpaperFormFields = {
    name: string;
    entity: Entity;
    provider: 'csv' | 'xero';
    includeTrialBalance?: boolean;
    trialBalance?: { code: string; debitTY: number | null; creditTY: number | null; debitLY: number | null; creditLY: number | null }[];
    carryForward?: boolean;
    priorWorkpaperId?: string;
    priorWorkpaperPeriodEnd?: string;
    accountingBasis: TrialBalanceAccountingBasisEnum;
    periodEnd: string;
    dueDate: string;
    procedureTemplateId: string;
    checklistId: string;
    customWorksheetTemplateIds?: string[];
    materialityLimit: number;
    materialityLimitType: 'monetary' | 'percentage';
    approvers: UserOverview[];
    reviewers: UserOverview[];
    preparers: UserOverview[];
    private: boolean;
    disableNext: boolean;
    fyiJob?: { id: string; name: string };
    permissions: { preparer: { canPostJournal: boolean }; reviewer: {}; approver: {}; guest: {} };
};

function NewWorkpaperForm(): JSX.Element {
    const [xeroError, setXeroError] = useState<{ name: string; message: string } | undefined>();
    const [errors, setErrors] = useState<string[]>([]);
    const [step, setStep] = useState(1);
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const createWorkpaperQuery = useCreateWorkpaper();
    const [_, setParams] = useSearchParams();
    const methods = useForm<NewWorkpaperFormFields>({
        defaultValues: {
            reviewers: [],
            preparers: [],
            materialityLimitType: 'monetary',
            accountingBasis: TrialBalanceAccountingBasisEnum.ACCRUAL,
            includeTrialBalance: true,
            carryForward: true,
            private: false,
            permissions: { preparer: { canPostJournal: false }, reviewer: {}, approver: {}, guest: {} },
            disableNext: false,
        },
        reValidateMode: 'onBlur',
    });

    useEffect(() => {
        setParams((prev) => {
            prev.set('step', step.toString());
            return prev;
        });
    }, [setParams, step]);

    const entity = methods.watch('entity');

    function handleSubmit(): void {
        const values = methods.getValues();
        setXeroError(undefined);
        setErrors([]);

        // Check if due date is before periodEnd date and currentDate
        if (new Date(values.dueDate) < new Date(values.periodEnd)) {
            methods.setError('dueDate', { message: 'Due date must be later than the period end date.' });
            setErrors(['Due date must be later than the period end date.']);
            return;
        }

        createWorkpaperQuery.mutate(
            {
                permissions: values.permissions,
                entityId: values.entity.id,
                dueDate: values.dueDate,
                periodEnd: values.periodEnd,
                name: values.name,

                materialityLimit: values.materialityLimit
                    ? { amount: values.materialityLimit, isPercentage: values.materialityLimitType === 'percentage' }
                    : undefined,
                assigned: [
                    ...values.approvers.map((a) => ({ role: WorkpaperRoleEnum.APPROVER, userId: a.id })),
                    ...values.reviewers.map((r) => ({ role: WorkpaperRoleEnum.REVIEWER, userId: r.id })),
                    ...values.preparers.map((p) => ({ role: WorkpaperRoleEnum.PREPARER, userId: p.id })),
                ],
                private: values.private,
                accountingBasis: values.includeTrialBalance ? values.accountingBasis : TrialBalanceAccountingBasisEnum.ACCRUAL,
                integrations: { fyiJob: values.fyiJob?.id },
                checklistId: values.checklistId,
                procedureTemplateId: values.procedureTemplateId,
                templates: {
                    calculationIds: values.customWorksheetTemplateIds ?? [],
                },
                priorWorkpaperId: values.priorWorkpaperId ?? undefined,
                provider: !values.includeTrialBalance ? null : values.provider === 'xero' ? 'XERO' : null,
                trialBalance: values.includeTrialBalance ? values.trialBalance : undefined,
            },
            {
                onSuccess: ({ results }) => {
                    if (results[0].data?.id) {
                        navigate(`/${shortId}/workpapers/${results[0].data.id}/dashboard`);
                        toastNotification('success', `Successfully created ${values.name}!`);
                    }
                },
                onError: (res) => {
                    const results = res.response?.data?.results ?? [];
                    const fields = results[0]?.error?.fields;

                    const name = fields?.error;
                    const message = fields?.message;

                    if (name && message) {
                        setXeroError({ message, name });
                    } else {
                        setErrors(fields ? Object.values(fields) : []);
                    }

                    toastNotification('error', `Unable to create workpaper ${values.name}!`);
                },
            }
        );
    }

    return (
        <>
            {xeroError && (
                <AlertV2 title={xeroError.name} description={xeroError.message} type="warn" className="mb-4">
                    <AlertV2.Action
                        label="Reconnect to Xero"
                        onClick={(): void => navigate(`/${shortId}/entities/${entity.id}?tab=integrations&focusedIntegration=xero`)}
                    />
                    <AlertV2.ExternalLink
                        href={'https://support.cimplico.com/en/collections/7176340-workpapers-integrations'}
                        label="Learn more"
                    />
                </AlertV2>
            )}
            {errors.length > 0 && (
                <div className="w-fit mx-auto mb-16">
                    <AlertV2
                        title="Unable to create the workpaper"
                        description={errors.join('\n')}
                        type="error"
                        onClose={(): void => setErrors([])}
                    ></AlertV2>
                </div>
            )}
            <div className="max-w-[450px] mx-auto">
                <FormProvider {...methods}>
                    <div>
                        {step === 1 && (
                            <NewWorkpaperStepOne
                                onNext={(): void => {
                                    setStep(2);
                                    setXeroError(undefined);
                                }}
                            />
                        )}
                    </div>
                    <div>{step === 2 && <NewWorkpaperStepTwo onNext={(): void => setStep(3)} onPrev={(): void => setStep(1)} />}</div>
                    <div>
                        {step === 3 && (
                            <NewWorkpaperStepThree
                                onPrev={(): void => setStep(2)}
                                onNext={(): void => setStep(4)}
                                onSubmit={handleSubmit}
                                isSubmitting={createWorkpaperQuery.isLoading}
                            />
                        )}
                        {step === 4 && (
                            <NewWorkpaperStepFour
                                onPrev={(): void => setStep(3)}
                                onSubmit={handleSubmit}
                                isSubmitting={createWorkpaperQuery.isLoading}
                            />
                        )}
                    </div>
                </FormProvider>
            </div>
        </>
    );
}
export default NewWorkpaperForm;
